"use client"

import React, { useState, useCallback } from "react"
import { Stepper, Step, StepLabel, Button, Typography, Box, Grid, Card, CardContent, Divider } from "@mui/material"
import ExpandableCategory from "./UpdateExpandableCategory"
import { validateEmail, validatePassword } from "../../../utils/formUtils"
import { providerStructure as columns } from "../../../data_formats"
import UpdateFormField from "./updateFormField"

const steps = ["Basic Information", "Delivery & Payment", "Content", "Status Settings"]

const expandableCategories = [
  {
    id: "companyPresentation",
    otherId: "company_presentation_",
    title: "Company Presentation",
    buttonText: "ADD COMPANY PRESENTATION",
  },
  {
    id: "jobAdvertisement",
    otherId: "job_advertisement_",
    title: "Job Advertisement",
    buttonText: "ADD JOB ADVERTISEMENT",
  },
  {
    id: "flyer",
    otherId: "flyer_",
    title: "Flyer",
    buttonText: "ADD FLYER",
  },
  {
    id: "advertisement",
    otherId: "advertisement_",
    title: "Advertisement",
    buttonText: "ADD Advertisement",
  },
  {
    id: "menu",
    otherId: "menu_",
    title: "Menu",
    buttonText: "ADD MENU",
  },
  {
    id: "info",
    otherId: "info_",
    title: "Info",
    buttonText: "ADD INFO",
  },
  {
    id: "event",
    otherId: "event_",
    title: "Event",
    buttonText: "ADD EVENT",
  },
  {
    id: "advertisingVideo",
    otherId: "advertising_video_",
    title: "Advertisement Video",
    buttonText: "ADD ADVERTISEMENT VIDEO",
  },
]

const sections = {
  0: {
    basicInfo: ["providerName", "email", "password", "verifyPassword"],
    locationInfo: ["address", "postcode", "number", "region", "branch"],
    contactInfo: ["telephone", "domain", "description", "community"],
  },
  1: {
    deliverySettings: [
      "deliveryCircle",
      "deliveryCost",
      "deliveryApproxTime",
      "minOrderCost",
      "openTime",
      "pickup",
      "delivery",
    ],
    paymentMethods: ["paypalMode", "paypalClientId", "paypalClientSecret", "cashMode"],
  },
  2: expandableCategories.reduce((acc, category) => {
    acc[category.id] = columns
      .filter(
        (col) =>
          col.field &&
          (col.field.startsWith(category.id) ||
            col.field.startsWith(category?.otherId) ||
            (category.id == 'companyPresentation' ? col.field.startsWith("companyWelcome"):undefined)),
      )
      .map((col) => col.field)
    return acc
  }, {}),
  3: {
    statusSettings: ["Abholung", "Lieferung", "iswelcome", "availability", "emailVerified", "deactivate", "logo"],
  },
}

const sectionTitles = {
  basicInfo: "Basic Information",
  locationInfo: "Location Information",
  contactInfo: "Contact Information",
  deliverySettings: "Delivery Settings",
  paymentMethods: "Payment Methods",
  companyPresentation: "Company Presentation",
  jobAdvertisement: "Job Advertisement",
  flyer: "Flyer",
  advertisement: "Advertisement",
  menu: "Menu",
  info: "Info",
  event: "Event",
  advertisingVideo: "Advertisement Video",
  statusSettings: "Status Settings",
}

const ProviderForm = ({ Provider, enableLoader }) => {
  const [activeStep, setActiveStep] = useState(0)
  const [values, setValues] = useState(Provider || {})
  const [expandedCategories, setExpandedCategories] = useState(() => {
    const expanded = new Map()
    expandableCategories.forEach((category) => {
      const categoryData = Provider?.posts?.[`${category.id}s`]
      if (categoryData && categoryData.length > 0) {
        expanded.set(category.id, {
          sections: categoryData,
          expanded: true,
        })
      }
    })
    return expanded
  })
  const [categorySections, setCategorySections] = useState(new Map())

  const validateStep = useCallback(
    (step) => {
      switch (step) {
        case 0:
          if (!values.providerName) {
            alert("Please enter provider name")
            return false
          }
          if (!validateEmail(values.email)) {
            alert("Please enter a valid email address")
            return false
          }
         
          return true

        case 1:
          if (!values.deliveryCost) {
            alert("Please provide Delivery cost")
            return false
          }
          if (!values.minOrderCost) {
            alert("Please provide Minimum Order cost")
            return false
          }
          return true
        case 2:
          // Validate expandable categories
          // for (const category of expandableCategories) {
          //   const feedShowStatus = values[`${category.otherId}feed_show_status`]
          //   if (feedShowStatus) {
          //     const subjectLine = values[`${category.otherId}subject_line`]
          //     const inputText = values[`${category.otherId}input_text`]
          //     const picture = values[`${category.otherId}picture_1`]
          //     const startDay = values[`${category.id}WelcomeStartDay`]
          //     const endDay = values[`${category.id}WelcomeEndDay`]

          //     if (!subjectLine || !inputText || !picture || !startDay || !endDay) {
          //       alert(`Please provide all details for ${category.title}`)
          //       return false
          //     }

          //     if (new Date(startDay) >= new Date(endDay)) {
          //       alert(`Please provide a valid date range for ${category.title}`)
          //       return false
          //     }
          //   }
          // }

          return true
        default:
          return true
      }
    },
    [values],
  )

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1)
    }
  }, [activeStep, validateStep])

  const handleBack = useCallback(() => {
    setActiveStep((prevStep) => prevStep - 1)
  }, [])

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()

      const formData = new FormData()
      const categoryData = {}

      Object.keys(values).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) {
          const [category, sectionId, field] = key.split("-")

          if (expandableCategories.some((cat) => cat.id === category)) {
            if (!categoryData[category]) {
              categoryData[category] = []
            }

            let section = categoryData[category].find((s) => s.id === sectionId)
            if (!section) {
              section = { id: sectionId }
              categoryData[category].push(section)
            }

            if (values[key] instanceof File) {
              // Handle file separately
              formData.append(`${category}-${sectionId}-${field}`, values[key])
              section[field] = `${category}-${sectionId}-${field}` // Store a reference
            } else {
              section[field] = values[key]
            }
          } else {
            if (values[key] instanceof File) {
              formData.append(key, values[key])
            } else {
              formData.append(key, values[key])
            }
          }
        }
      })

      // Append category data to formData
      Object.keys(categoryData).forEach((category) => {
        formData.append(category, JSON.stringify(categoryData[category]))
      })
      console.log("🚀 ~ Object.keys ~ categoryData:", categoryData)

      enableLoader()
      // addProvider(formData, { 'Content-Type': 'multipart/form-data' });
    },
    [values, enableLoader],
  )

  const handleExpandCategory = useCallback(
    (categoryId) => {
      setExpandedCategories((prev) => {
        const newMap = new Map(prev)
        newMap.set(categoryId, {
          sections: categorySections.get(categoryId) || [],
          expanded: true,
        })
        return newMap
      })
    },
    [categorySections],
  )

  const handleRemoveCategory = useCallback((categoryId) => {
    setExpandedCategories((prev) => {
      const newMap = new Map(prev)
      newMap.delete(categoryId)
      return newMap
    })
  }, [])

  const handleToggleExpand = useCallback((categoryId) => {
    setExpandedCategories((prev) => {
      const newMap = new Map(prev)
      const category = newMap.get(categoryId)
      if (category) {
        category.expanded = !category.expanded
      }
      return newMap
    })
  }, [])

  const handleSaveSections = useCallback((categoryId, sections) => {
    setCategorySections((prev) => {
      const newMap = new Map(prev)
      newMap.set(categoryId, sections)
      return newMap
    })
  }, [])

  const renderSection = useCallback(
    (sectionName, fields) => {
      if (expandableCategories.some((cat) => cat.id === sectionName)) {
        if (expandedCategories.has(sectionName)) {
          return (
            <ExpandableCategory
              key={sectionName}
              title={sectionTitles[sectionName]}
              fields={fields}
              values={values}
              setValues={setValues}
              columns={columns}
              onRemove={() => handleRemoveCategory(sectionName)}
              categoryId={sectionName}
              expanded={expandedCategories.get(sectionName)?.expanded ?? true}
              onToggleExpand={() => handleToggleExpand(sectionName)}
              savedSections={categorySections.get(sectionName)}
              onSaveSections={(sections) => handleSaveSections(sectionName, sections)}
            />
          )
        } else {
          const categoryData = expandableCategories.find((cat) => cat.id === sectionName)
          return (
            <Button
              key={sectionName}
              onClick={() => handleExpandCategory(sectionName)}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              {categoryData ? categoryData.buttonText : `Add ${sectionTitles[sectionName]}`}
            </Button>
          )
        }
      }

      return (
        <Card variant="outlined" sx={{ mb: 3 }} key={sectionName}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary">
              {sectionTitles[sectionName]}
            </Typography>
            <Grid container spacing={2}>
              {fields.map((fieldName) => {
                const field = columns.find((col) => col.field === fieldName)
                if (!field) return null

                if (field.inputType === "heading") {
                  return (
                    <Grid item xs={12} key={fieldName}>
                      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                        {field.title}
                      </Typography>
                    </Grid>
                  )
                }

                return (
                  <Grid
                    item
                    xs={12}
                    sm={field.inputType === "multiText" || field.inputType === "file" ? 12 : 6}
                    md={field.inputType === "multiText" || field.inputType === "file" ? 12 : 4}
                    key={fieldName}
                  >
                    <UpdateFormField
                      structure={field}
                      id="add"
                      values={values}
                      setValues={setValues}
                      showPaypalFields={values.paypalMode}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </CardContent>
        </Card>
      )
    },
    [
      expandableCategories,
      expandedCategories,
      values,
      columns,
      handleRemoveCategory,
      handleToggleExpand,
      categorySections,
      handleSaveSections,
      handleExpandCategory,
    ],
  )

  const renderContentStep = useCallback(() => {
    return (
      <Box>
        {expandableCategories.map((category) => (
          <Box key={category.id}>
            {expandedCategories.has(category.id) ? (
              <ExpandableCategory
                key={category.id}
                title={category.title}
                fields={sections[2][category.id]}
                values={values}
                setValues={setValues}
                columns={columns}
                onRemove={() => handleRemoveCategory(category.id)}
                categoryId={category.id}
                expanded={expandedCategories.get(category.id)?.expanded ?? true}
                onToggleExpand={() => handleToggleExpand(category.id)}
                savedSections={categorySections.get(category.id)}
                onSaveSections={(sections) => handleSaveSections(category.id, sections)}
              />
            ) : (
              <Button onClick={() => handleExpandCategory(category.id)} variant="outlined" sx={{ mr: 2, mb: 2 }}>
                {category.buttonText}
              </Button>
            )}
          </Box>
        ))}
      </Box>
    )
  }, [
    expandableCategories,
    expandedCategories,
    values,
    columns,
    handleRemoveCategory,
    handleToggleExpand,
    categorySections,
    handleSaveSections,
    handleExpandCategory,
  ])

  const renderStepContent = useCallback(
    (step) => {
      if (step === 2) {
        // Content step
        return renderContentStep()
      }

      const stepSections = sections[step]
      return <>{Object.entries(stepSections).map(([sectionName, fields]) => renderSection(sectionName, fields))}</>
    },
    [renderContentStep, renderSection],
  )

  return (
    <>
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          mb: 4,
          "& .MuiStepLabel-root .Mui-completed": {
            color: "primary.main",
          },
          "& .MuiStepLabel-label.Mui-completed.MuiStepLabel-alternativeLabel": {
            color: "primary.main",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box>
        {activeStep === steps.length ? (
          <Box textAlign="center" py={4}>
            <Typography variant="h5" gutterBottom>
              All steps completed
            </Typography>
            <Typography color="text.secondary">Your provider has been successfully added</Typography>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 4 }}>{renderStepContent(activeStep)}</Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined" size="large">
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
              >
                {activeStep === steps.length - 1 ? "Submit" : "Next"}
              </Button>
            </Box>
          </form>
        )}
      </Box>
    </>
  )
}

export default React.memo(ProviderForm)

